<template>
  <LandingBackground>
    <PublicLandingHeader
      :title="appSettingsStore.applicationTitle"
      subtitle="Nevada Division of Environmental Protection"
      light
    />

    <PublicLandingContainer class="grid justify-start gap-16 xl:grid-cols-2 2xl:grid-cols-3">
      <PublicLandingBackdrop class="grid grid-cols-1 gap-10 2xl:col-span-2 2xl:grid-cols-2">
        <PublicLandingOnlineServices
          class="2xl:col-span-2"
          title="Online Services"
          body="The Sustainability Management and Reporting Tool (SMART) offers online recycling and solid waste disposal reporting to the Bureau of Sustainable Materials Management."
        ></PublicLandingOnlineServices>
        <PublicLandingCard>
          <PublicLandingCardSection label="Recycling">
            <PublicLandingList>
              <PublicLandingListItem label="Submit annual recycling reports" />
              <PublicLandingListItem label="View historical recycled and diverted materials" />
              <PublicLandingListItem label="Secure access to your company or agency data" />
            </PublicLandingList>
          </PublicLandingCardSection>
        </PublicLandingCard>
        <PublicLandingCard>
          <PublicLandingCardSection label="Solid Waste Disposal">
            <PublicLandingList>
              <PublicLandingListItem label="Submit quarterly, semi-annual, and annual disposal reports" />
              <PublicLandingListItem label="View historical disposal data" />
              <PublicLandingListItem label="Secure access to your facility data" />
            </PublicLandingList>
          </PublicLandingCardSection>
        </PublicLandingCard>
      </PublicLandingBackdrop>

      <PublicLandingBackdrop class="grid gap-10 self-start">
        <PublicLandingCard>
          <PublicLandingCardSection label="Public Records">
            <PublicLandingParagraph> Solid Waste </PublicLandingParagraph>
            <PublicLandingList class="mb-3">
              <PublicLandingListItem>
                <span class="">
                  Search
                  <router-link
                    :to="{ name: routeNames.facilitySite.facilitySites }"
                    class="text-sky-600 hover:underline"
                  >
                    Facilities</router-link
                  >,
                  <router-link :to="{ name: routeNames.permit.permits }" class="text-sky-600 hover:underline">
                    Permits</router-link
                  >,
                  <router-link :to="{ name: routeNames.inspection.inspections }" class="text-sky-600 hover:underline">
                    Inspections</router-link
                  >, or
                  <router-link
                    :to="{ name: routeNames.disposalReport.disposalReports }"
                    class="text-sky-600 hover:underline"
                  >
                    Disposal Reports</router-link
                  >
                </span>
              </PublicLandingListItem>
              <PublicLandingListItem>
                <span class="">
                  Run
                  <router-link
                    :to="{ name: routeNames.analytics.exportFacilityData }"
                    class="text-sky-600 hover:underline"
                  >
                    Facility Data Export</router-link
                  >,
                  <router-link
                    :to="{ name: routeNames.analytics.generatedWasteReport }"
                    class="text-sky-600 hover:underline"
                  >
                    Generated Waste Report</router-link
                  >,
                  <router-link
                    :to="{ name: routeNames.analytics.divertedWasteReport }"
                    class="text-sky-600 hover:underline"
                  >
                    Diverted Waste Report</router-link
                  >, or
                  <router-link
                    :to="{ name: routeNames.analytics.exportDisposalReportData }"
                    class="text-sky-600 hover:underline"
                  >
                    Disposal Report Data Export</router-link
                  >
                </span>
              </PublicLandingListItem>
            </PublicLandingList>
            <PublicLandingParagraph> Sustainability </PublicLandingParagraph>
            <PublicLandingList class="mb-3">
              <PublicLandingListItem>
                <span class="">
                  Search
                  <router-link
                    :to="{ name: routeNames.organization.organizations }"
                    class="text-sky-600 hover:underline"
                  >
                    Organizations</router-link
                  >
                  or
                  <router-link
                    :to="{ name: routeNames.recyclingReport.recyclingReports }"
                    class="text-sky-600 hover:underline"
                  >
                    Recycling Reports</router-link
                  >
                </span>
              </PublicLandingListItem>

              <PublicLandingListItem>
                <span class="">
                  Run
                  <router-link
                    :to="{ name: routeNames.analytics.divertedMaterialReport }"
                    class="text-sky-600 hover:underline"
                  >
                    Diverted Material Report</router-link
                  >,
                  <router-link
                    :to="{ name: routeNames.analytics.materialCategoryReport }"
                    class="text-sky-600 hover:underline"
                  >
                    Material Category Report</router-link
                  >,
                  <router-link
                    :to="{ name: routeNames.analytics.organizationMaterialReport }"
                    class="text-sky-600 hover:underline"
                  >
                    Organization Material Report</router-link
                  >,
                  <router-link
                    :to="{ name: routeNames.analytics.recycledMaterialReport }"
                    class="text-sky-600 hover:underline"
                  >
                    Reycling Material Report</router-link
                  >,
                  <router-link
                    :to="{ name: routeNames.analytics.recyclingRateReport }"
                    class="text-sky-600 hover:underline"
                  >
                    Recycling Rate Report</router-link
                  >, or
                  <router-link
                    :to="{ name: routeNames.analytics.exportRecyclingReportData }"
                    class="text-sky-600 hover:underline"
                  >
                    Recycling Report Data Export</router-link
                  >
                </span>
              </PublicLandingListItem>
            </PublicLandingList>
          </PublicLandingCardSection>
        </PublicLandingCard>
      </PublicLandingBackdrop>
    </PublicLandingContainer>

    <MdrnFooter light black-and-white />
  </LandingBackground>
</template>

<script lang="ts">
import LandingBackground from "@/components/LandingBackground.vue";
import { routeNames } from "@/routes";
import { MdrnFooter, useAppSettingsStore } from "@modernary/mui/components/common";
import {
  PublicLandingBackdrop,
  PublicLandingCard,
  PublicLandingCardSection,
  PublicLandingContainer,
  PublicLandingHeader,
  PublicLandingList,
  PublicLandingListItem,
  PublicLandingOnlineServices,
  PublicLandingParagraph,
} from "@modernary/mui/components/publicLanding";
import { defineComponent } from "vue";

// Home content for unauth'd users
export default defineComponent({
  name: "HomePublic",
  setup() {
    const appSettingsStore = useAppSettingsStore();

    return { appSettingsStore, routeNames };
  },
  components: {
    MdrnFooter,
    PublicLandingCard,
    PublicLandingCardSection,
    PublicLandingList,
    PublicLandingListItem,
    PublicLandingParagraph,
    PublicLandingOnlineServices,
    PublicLandingHeader,
    PublicLandingContainer,
    PublicLandingBackdrop,
    LandingBackground,
  },
});
</script>

<style lang="css" module></style>
